import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import useCepForm from './CepForm.hook';
import ConfigurationStep from './Steps/ConfigurationStep';
import TacticsStep from './Steps/TacticsStep/TacticsStep';
import { CepStateType } from 'basics/types/maya.types';
import ButtonsBar from 'components/ButtonsBar';
import { CepFormPropsType, CepFormStepsIndex } from 'components/CepForm/CepForm.types';
import GoalStep from 'components/CepForm/Steps/GoalStep';
import MiViewPreviewStep from 'components/CepForm/Steps/MiViewPreviewStep';
import TargetListStep from 'components/CepForm/Steps/TargetListStep';
import {
  FormLayoutContainer,
  FormLayoutMidPanel,
  FormLayoutMidPanelContent,
  FormLayoutMidPanelHeader,
  FormLayoutRightPanel,
} from 'components/FormLayout';
import SideFormDetails from 'components/SideFormDetails';
import TacticFieldsValidationDialog from 'components/TacticFieldsValidationDialog';
import TacticValidationDialog from 'components/TacticValidationDialog';
import cepState from 'states/cep.states';
import Box from 'yoda-ui/components/Box';
import ConfirmationDialog from 'yoda-ui/components/Dialogs/ConfirmationDialog';
import Stepper from 'yoda-ui/components/Stepper/Stepper';
import { YodaFormProvider } from 'yoda-ui/yodaForm';

const CepForm: FC<CepFormPropsType> = ({ submitCallback, isTemplate = false }) => {
  const {
    handleChangeStep,
    yodaFormMethods,
    stepIndexer,
    buttonsConfig,
    stepsList,
    syncTargetsLoading,
    openConfirmationModal,
    handleProceed,
    handleCancel,
    openValidationDialog,
    setOpenValidationDialog,
    openFieldsValidationDialog,
    setOpenFieldsValidationDialog,
    invalidTactics,
    cepDateError,
    validationError,
  } = useCepForm(submitCallback, isTemplate);
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const flags = useFlags();
  const { t } = useTranslation();

  return (
    <YodaFormProvider { ...yodaFormMethods.providerFields }>
      <FormLayoutContainer>
        <FormLayoutMidPanel >
          <FormLayoutMidPanelHeader>
            <Stepper
              handleChangeStep={ handleChangeStep }
              stepsList={ stepsList }
              stepIndex={ stepIndexer.stepIndex }
            />
          </FormLayoutMidPanelHeader>
          <FormLayoutMidPanelContent>
            {
              stepIndexer.stepIndex === CepFormStepsIndex.configurationStep
                ? (
                  <Box width="100%">
                    <ConfigurationStep isTemplate={ isTemplate }/>
                  </Box>
                )
                : null
            }
            {
              stepIndexer.stepIndex === CepFormStepsIndex.configurationStep
              && flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
              && flags.mayaBdcfcsd1251EnableEditingEndDateOfApprovedCep
              && openConfirmationModal
              && <ConfirmationDialog
                dialogTitle={ t('maya_cep_endDate_editing_warning') }
                onConfirm={ handleProceed }
                onConfirmLabel={ t('actions_proceed_label') }
                onDismiss={ handleCancel }
                onDismissLabel={ t('actions_cancel_label') }
              >
              </ConfirmationDialog>
            }
            {
              stepIndexer.stepIndex === CepFormStepsIndex.targetListStep
                ? (
                  <Box width="100%">
                    <TargetListStep syncTargetsLoading={ syncTargetsLoading }/>
                  </Box>
                )
                : null
            }
            {
              stepIndexer.stepIndex === CepFormStepsIndex.tacticsStep
                ? (
                  <Box width="100%">
                    <TacticsStep isTemplate={ isTemplate }/>
                  </Box>
                )
                : null
            }
            {
              flags.mayaBiogenlinc4793EnableGoalStep && stepIndexer.stepIndex === CepFormStepsIndex.goalStep
                ? (
                  <Box width="100%">
                    <GoalStep />
                  </Box>
                )
                : null
            }
            {
              flags.mayaBdcfcsd714EnableMiViewPreview && stepIndexer.stepIndex === CepFormStepsIndex.miViewPreviewStep
                ? (
                  <Box width="100%">
                    <MiViewPreviewStep />
                  </Box>
                )
                : null
            }
            { buttonsConfig && <ButtonsBar { ...buttonsConfig } /> }
            <TacticValidationDialog
              title={ t('maya_tactic_form_validation_dialog_title') }
              open={ openValidationDialog }
              setOpen={ setOpenValidationDialog }
            >
            </TacticValidationDialog>
            <TacticFieldsValidationDialog
              open={ openFieldsValidationDialog }
              setOpen={ setOpenFieldsValidationDialog }
              invalidTactics={ invalidTactics }
              cepDateError={ cepDateError }
              validationError={ validationError }
              cepId={ cepStateValue?.cep?._id }
            />
          </FormLayoutMidPanelContent>
        </FormLayoutMidPanel>

        <FormLayoutRightPanel >
          <SideFormDetails />
        </FormLayoutRightPanel>

      </FormLayoutContainer>
    </YodaFormProvider>
  );
};

export default CepForm;
